export const SEMANTIC_STATUS_TONES = ['ok', 'warn', 'danger', 'neutral', 'info'];

const normalize = (value) => String(value || '').trim().toLowerCase();

const toTone = (value, fallback = 'neutral') => {
  const v = String(value || '').trim().toLowerCase();
  if (SEMANTIC_STATUS_TONES.includes(v)) return v;
  return fallback;
};

const customerLifecycleToneMap = {
  active: 'ok',
  activated: 'ok',
  online: 'ok',
  connected: 'ok',
  pending: 'warn',
  pending_activation: 'warn',
  suspended: 'danger',
  inactive: 'danger',
  terminated: 'danger',
  overdue: 'danger',
  disabled: 'neutral',
  draft: 'neutral',
  unknown: 'neutral',
};

const customerHealthToneMap = {
  healthy: 'ok',
  online: 'ok',
  operational: 'ok',
  warning: 'warn',
  degraded: 'warn',
  maintenance: 'warn',
  packet_loss: 'danger',
  offline: 'danger',
  down: 'danger',
  critical: 'danger',
  unknown: 'neutral',
};

const infrastructureToneMap = {
  active: 'ok',
  online: 'ok',
  healthy: 'ok',
  up: 'ok',
  maintenance: 'warn',
  warning: 'warn',
  degraded: 'warn',
  unstable: 'warn',
  high_load: 'warn',
  offline: 'danger',
  down: 'danger',
  failed: 'danger',
  error: 'danger',
  inactive: 'neutral',
  unknown: 'neutral',
  default: 'neutral',
};

const monitoringToneMap = {
  healthy: 'ok',
  normal: 'ok',
  online: 'ok',
  stable: 'ok',
  recovered: 'ok',
  degraded: 'warn',
  warning: 'warn',
  maintenance: 'warn',
  flapping: 'warn',
  high_load: 'warn',
  running: 'info',
  queued: 'info',
  syncing: 'info',
  collecting: 'info',
  critical: 'danger',
  down: 'danger',
  offline: 'danger',
  failure: 'danger',
  failed: 'danger',
  escalated: 'danger',
  error: 'danger',
  unknown: 'neutral',
};

const auditToneMap = {
  success: 'ok',
  succeeded: 'ok',
  completed: 'ok',
  done: 'ok',
  normal: 'ok',
  warning: 'warn',
  warn: 'warn',
  pending: 'warn',
  retry: 'warn',
  running: 'info',
  in_progress: 'info',
  processing: 'info',
  info: 'info',
  failed: 'danger',
  failure: 'danger',
  error: 'danger',
  rejected: 'danger',
  critical: 'danger',
  default: 'neutral',
  unknown: 'neutral',
};

const applicationToneMap = {
  submitted: 'info',
  under_review: 'warn',
  needs_revision: 'danger',
  approved: 'ok',
  rejected: 'danger',
  draft: 'neutral',
  unknown: 'neutral',
  default: 'neutral',
};

const mapByDictionary = (rawStatus, dictionary, fallback = 'neutral') => {
  const normalized = normalize(rawStatus).replace(/\s+/g, '_');
  return toTone(dictionary[normalized] || dictionary.default || fallback, fallback);
};

export const mapCustomerStatusToTone = (rawStatus) => mapByDictionary(rawStatus, customerLifecycleToneMap, 'neutral');

export const mapCustomerHealthToTone = (rawStatus) => mapByDictionary(rawStatus, customerHealthToneMap, 'neutral');

export const mapInfrastructureStatusToTone = (rawStatus) => mapByDictionary(rawStatus, infrastructureToneMap, 'neutral');

export const mapMonitoringStatusToTone = (rawStatus) => mapByDictionary(rawStatus, monitoringToneMap, 'neutral');

export const mapAuditStatusToTone = (rawStatus) => mapByDictionary(rawStatus, auditToneMap, 'neutral');

export const mapApplicationStatusToTone = (rawStatus) => mapByDictionary(rawStatus, applicationToneMap, 'neutral');

export const mapStatusToTone = (domain, rawStatus) => {
  const normalizedDomain = normalize(domain);
  if (normalizedDomain === 'customer') return mapCustomerStatusToTone(rawStatus);
  if (normalizedDomain === 'customer_health') return mapCustomerHealthToTone(rawStatus);
  if (normalizedDomain === 'infrastructure' || normalizedDomain === 'infra') return mapInfrastructureStatusToTone(rawStatus);
  if (normalizedDomain === 'monitoring') return mapMonitoringStatusToTone(rawStatus);
  if (normalizedDomain === 'audit') return mapAuditStatusToTone(rawStatus);
  if (normalizedDomain === 'application' || normalizedDomain === 'applications' || normalizedDomain === 'registration') {
    return mapApplicationStatusToTone(rawStatus);
  }
  return 'neutral';
};

const toneBadgeClassMap = {
  ok: 'text-emerald-200 border-emerald-500/40 bg-emerald-500/10',
  warn: 'text-amber-200 border-amber-500/40 bg-amber-500/10',
  danger: 'text-rose-200 border-rose-500/40 bg-rose-500/10',
  info: 'text-cyan-200 border-cyan-500/40 bg-cyan-500/10',
  neutral: 'text-slate-300 border-slate-500/40 bg-slate-600/10',
};

const toneTextClassMap = {
  ok: 'text-emerald-300',
  warn: 'text-amber-300',
  danger: 'text-rose-300',
  info: 'text-cyan-300',
  neutral: 'text-gray-400',
};

const toneColorMap = {
  ok: '#22c55e',
  warn: '#f59e0b',
  danger: '#ef4444',
  info: '#3b82f6',
  neutral: '#9ca3af',
};

export const semanticToneToBadgeClass = (tone) => toneBadgeClassMap[toTone(tone)] || toneBadgeClassMap.neutral;

export const semanticToneToTextClass = (tone) => toneTextClassMap[toTone(tone)] || toneTextClassMap.neutral;

export const semanticToneToColor = (tone) => toneColorMap[toTone(tone)] || toneColorMap.neutral;
